import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useAuthPopup } from './useAuthPopup';
import { API_URL } from '../config/api';

/**
 * Custom hook for save/unsave (favorite) property toggle.
 * - Auto-checks saved status on mount when user is logged in
 * - Optimistic UI updates (instant feedback, rolls back on failure)
 * - Returns error state for toast notifications
 * - Blocks interaction while initial status check is in-flight
 *
 * @param {number|string} propertyId
 * @param {object} [options]
 * @param {boolean} [options.initialSaved=false] - Known initial saved state (skips API check if true)
 * @param {function} [options.onToggle] - Callback: (propertyId, saved) => void
 * @returns {{ saved, saving, error, toggleSave }}
 */
const useSaveProperty = (propertyId, { initialSaved = false, onToggle } = {}) => {
    const { user, session } = useAuth();
    const { openAuthPopup } = useAuthPopup();
    const [saved, setSaved] = useState(initialSaved);
    const [saving, setSaving] = useState(false);
    const [checking, setChecking] = useState(!initialSaved && !!user);
    const [error, setError] = useState(null);

    // Check saved status on mount
    useEffect(() => {
        if (!user || !propertyId || initialSaved) {
            setChecking(false);
            return;
        }
        let cancelled = false;
        setChecking(true);
        const checkSaved = async () => {
            try {
                const res = await fetch(`${API_URL}/saved-properties/${propertyId}/status`, {
                    headers: { 'Authorization': `Bearer ${session?.access_token}` }
                });
                if (res.ok && !cancelled) {
                    const data = await res.json();
                    setSaved(data.saved);
                }
            } catch {
                // Not critical
            } finally {
                if (!cancelled) setChecking(false);
            }
        };
        checkSaved();
        return () => { cancelled = true; };
    }, [user, propertyId, session, initialSaved]);

    const toggleSave = useCallback(async (e) => {
        if (e) e.stopPropagation();
        if (!user) {
            openAuthPopup();
            return;
        }
        if (saving || checking) return; // Block during check or save

        // Optimistic update
        const previousSaved = saved;
        setSaved(!saved);
        setSaving(true);
        setError(null);

        try {
            const res = await fetch(`${API_URL}/saved-properties/${propertyId}`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${session?.access_token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setSaved(data.saved); // Sync with server truth
                if (onToggle) onToggle(propertyId, data.saved);
            } else {
                setSaved(previousSaved);
                setError('Failed to update save status');
            }
        } catch (err) {
            setSaved(previousSaved);
            setError('Network error — please try again');
        } finally {
            setSaving(false);
        }
    }, [user, saved, saving, checking, session, propertyId, openAuthPopup, onToggle]);

    // `saving` exposed to consumers includes both states — button is disabled during either
    return { saved, saving: saving || checking, error, toggleSave };
};

export default useSaveProperty;
